import { useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, MapPin, Mail, Phone } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const PROJECT_TYPES = ["New Custom Home", "The Cambridge", "Renovation / Extension", "Commercial Fit-out", "Other"];
const BUDGETS = ["Under $250k", "$250k – $500k", "$500k – $1M", "$1M – $2M", "$2M+"];

const inputCls = "w-full bg-white border border-black/15 px-4 py-3.5 text-sm text-black placeholder:text-black/35 focus:outline-none focus:border-gold transition-colors";
const labelCls = "block text-[10px] font-bold uppercase tracking-[0.25em] text-black/45 mb-2";

export default function ContactPage() {
  const [params] = useSearchParams();
  const topic = params.get("topic");
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    project_type: topic === "The Cambridge" ? "The Cambridge" : PROJECT_TYPES[0],
    location: "Masterton", budget: BUDGETS[1],
    message: topic ? `I'd like to know more about ${topic}.` : "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received. We'll be in touch within two working days.");
      setForm({ name: "", email: "", phone: "", project_type: PROJECT_TYPES[0], location: "Masterton", budget: BUDGETS[1], message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-white">
      <section className="pt-56 pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6">Contact</motion.p>
          <div className="overflow-hidden mb-16">
            <motion.h1 initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl"
              data-testid="contact-headline">
              Let's talk building.
            </motion.h1>
          </div>

          <div className="grid lg:grid-cols-12 gap-14">
            <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}
              className="lg:col-span-5">
              <p className="text-base sm:text-lg text-black/60 leading-relaxed max-w-md">
                Every build starts with a conversation. Tell us what you're planning and
                we'll come back within two working days with next steps.
              </p>
              <div className="mt-12 space-y-7 border-t border-black/10 pt-10">
                <div className="flex items-start gap-4">
                  <MapPin size={18} className="text-gold mt-0.5" strokeWidth={1.75} />
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/45">Location</div>
                    <div className="text-black mt-1" data-testid="contact-location-text">Masterton, Wairarapa, New Zealand</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail size={18} className="text-gold mt-0.5" strokeWidth={1.75} />
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/45">Email</div>
                    <div className="text-black mt-1">admin@lwandco.co.nz</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={18} className="text-gold mt-0.5" strokeWidth={1.75} />
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/45">Phone</div>
                    <div className="text-black mt-1">027 222 5926</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Instagram size={18} className="text-gold mt-0.5" strokeWidth={1.75} />
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/45">Instagram</div>
                    <a href="https://www.instagram.com/lwandco" target="_blank" rel="noopener noreferrer"
                      data-testid="contact-instagram-link" className="text-black mt-1 inline-block hover:text-gold transition-colors">
                      @lwandco
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.form initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.8 }}
              onSubmit={submit} data-testid="contact-form"
              className="lg:col-span-7 border border-black/10 p-8 sm:p-10 space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="contact-name">Your name *</label>
                  <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")}
                    placeholder="Jane Smith" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="contact-email">Email *</label>
                  <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")}
                    placeholder="jane@example.co.nz" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="contact-phone">Phone</label>
                  <input id="contact-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")}
                    placeholder="021 000 000" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="contact-type">Project type *</label>
                  <select id="contact-type" data-testid="contact-type-select" value={form.project_type} onChange={set("project_type")} className={inputCls}>
                    {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="contact-location">Location</label>
                  <input id="contact-location" data-testid="contact-location-input" value={form.location} onChange={set("location")}
                    placeholder="Masterton" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="contact-budget">Budget range</label>
                  <select id="contact-budget" data-testid="contact-budget-select" value={form.budget} onChange={set("budget")} className={inputCls}>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="contact-message">About your project *</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")}
                  placeholder="Site, scope, timeline — anything you know so far." className={`${inputCls} resize-none`} />
              </div>
              <button data-testid="contact-submit-btn" type="submit" disabled={sending}
                className="w-full sm:w-auto px-10 py-4 bg-gold text-black font-bold text-sm uppercase tracking-[0.15em] hover:bg-black hover:text-white disabled:opacity-50 transition-colors duration-300">
                {sending ? "Sending…" : "Send Enquiry"}
              </button>
            </motion.form>
          </div>
        </div>
      </section>
    </main>
  );
}
